import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/hooks/useLanguage';
import { useCounty, useMunicipalities } from '@/hooks/usePostalAreas';
import {
  getCountyBySlug,
  getMunicipalitiesByCountyCode,
} from '@/lib/postal-snapshot';
import { PostalBreadcrumb } from '@/components/postal/PostalBreadcrumb';
import { PostalStatsCards } from '@/components/postal/PostalStatsCards';
import { PopulationChart } from '@/components/postal/PopulationChart';
import { IncomeChart } from '@/components/postal/IncomeChart';
import { PropertyChart } from '@/components/postal/PropertyChart';
import { PostalAreaGrid } from '@/components/postal/PostalAreaGrid';
import { SEO, getPlaceSchema, getBreadcrumbSchema } from '@/hooks/useSEO';

export default function PostalCountyPage() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { countySlug } = useParams<{ countySlug: string }>();

  // Read SEO-relevant fields from the build-time snapshot. Synchronous — works
  // during SSG render and on initial client render before React Query resolves.
  // If the slug isn't in the snapshot we still render an SEO-friendly shell so
  // Google doesn't see a soft 404 (the body falls back to a "not found" line).
  const countySnap = getCountyBySlug(countySlug);
  const muniSnaps = countySnap
    ? getMunicipalitiesByCountyCode(countySnap.code)
    : [];

  // Fresh data (with population_data / income_data / properties_data for
  // charts) is fetched client-side after hydration. The query is `enabled`
  // only after we have a code from the snapshot; otherwise it's a no-op.
  const { data: countyFresh } = useCounty(countySnap?.code ?? '');
  const { data: muniFresh } = useMunicipalities(countySnap?.code ?? '');

  // Prefer fresh data when it arrives; fall back to the snapshot for SSG.
  const county = countyFresh ?? countySnap;
  const municipalities = muniFresh ?? muniSnaps;

  if (!county) {
    return (
      <div className="container py-12">
        <p>{t('postal.notFound')}</p>
      </div>
    );
  }

  const countyPath = `/information/postnummer/${countySlug}`;
  const countyUrl = `https://postnummerservice.se/${lang}${countyPath}`;
  const seoDescription = `${county.name} har ${county.population_total.toLocaleString('sv-SE')} invånare, ${county.postcode_count} postnummer och en yta på ${county.area?.toLocaleString('sv-SE')} km². Se statistik om befolkning, inkomst och fastigheter.`;

  return (
    <div className="container py-6 md:py-12 lg:py-16">
      <SEO
        title={`${county.name} – ${t('navigation.postalCodes')}`}
        description={seoDescription}
        path={countyPath}
        jsonLd={[
          getBreadcrumbSchema([
            { name: t('common.home'), url: `https://postnummerservice.se/${lang}` },
            { name: t('navigation.postalCodes'), url: `https://postnummerservice.se/${lang}/information/postnummer` },
            { name: county.name, url: countyUrl },
          ]),
          getPlaceSchema({
            name: county.name,
            description: seoDescription,
            url: countyUrl,
            population: county.population_total,
            area: county.area ?? undefined,
          }),
        ]}
      />
      <div className="max-w-5xl mx-auto space-y-5 md:space-y-8">
        <PostalBreadcrumb levels={[{ label: county.name }]} />

        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{county.name}</h1>
          <p className="text-muted-foreground">{t('postal.countyDescription', { name: county.name })}</p>
          <p className="text-sm text-muted-foreground mt-2">{t('postal.countyIntro', { name: county.name, population: county.population_total.toLocaleString('sv-SE'), postalCodes: county.postcode_count })}</p>
        </div>

        <PostalStatsCards
          population={county.population_total}
          postalCodes={county.postcode_count}
          area={county.area ?? undefined}
          medianIncome={countyFresh?.income_data?.income_median}
        />

        {/* Charts need the heavy data (population_data, income_data,
            properties_data) that isn't in the snapshot. They render with
            client-side React Query data only — empty during SSG, filled in
            after hydration. */}
        {countyFresh && (
          <>
            <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
              <PopulationChart data={countyFresh.population_data} />
              <IncomeChart data={countyFresh.income_data} />
            </div>

            <PropertyChart data={countyFresh.properties_data} />
          </>
        )}

        {municipalities.length > 0 && (
          <PostalAreaGrid
            areas={municipalities}
            buildHref={(m) => `/${lang}/information/postnummer/${countySlug}/${m.slug ?? m.code}`}
            childLabel={t('postal.municipalities')}
          />
        )}
      </div>
    </div>
  );
}
